package fr.manyverse.app.presentation.components.modules.lockpicking

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawingPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Minimize
import androidx.compose.material3.Button
import androidx.compose.material3.FilledIconButton
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.TransformOrigin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import fr.manyverse.app.domain.model.ActiveLockpicking
import fr.manyverse.app.domain.model.LockpickingStatus
import fr.manyverse.app.domain.model.getLockpickingImages
import fr.manyverse.app.presentation.theme.success
import kotlinx.coroutines.delay
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.lockpicking_close
import manyverse.shared.generated.resources.lockpicking_minimize
import manyverse.shared.generated.resources.lockpicking_success_message
import manyverse.shared.generated.resources.lockpicking_timeout_message
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan2
import kotlin.random.Random
import kotlin.time.Clock
import kotlin.time.Duration.Companion.milliseconds

private const val ZONE_WIDTH_DEG = 18f
private const val HOLD_DURATION_MS = 1500L

/**
 * Fullscreen overlay shown to the player when a lockpicking challenge is active.
 * Displays a circular lock with a draggable pick. The player must hold the pick
 * in the target zone for ~1.5s to unlock each lock.
 */
@Composable
fun LockpickingPlayerOverlay(
    lockpicking: ActiveLockpicking,
    onUnlock: () -> Unit,
    onTimeout: () -> Unit,
    onClose: () -> Unit,
    onMinimize: () -> Unit,
    modifier: Modifier = Modifier
) {
    val (lockImageRes, pickImageRes) = getLockpickingImages()
    // Angle of the pick tip (degrees, 0 = top, clockwise). Starts at 0° (top of lock).
    // Finger position matches pick tip position.
    var pickAngle by remember(lockpicking.id) { mutableFloatStateOf(0f) }

    // Target zone angle (generated per lock, client-side only).
    // Excluded range around 0° (pick start) so the player must actually move the pick.
    var targetZoneAngle by remember(lockpicking.id, lockpicking.unlockedCount) {
        mutableFloatStateOf(randomAngleExcludingStart(startAngle = 0f, excludeMargin = 45f))
    }

    // Whether the pick is in the zone
    val isInZone = remember(pickAngle, targetZoneAngle) {
        val diff = angleDiff(pickAngle, targetZoneAngle)
        abs(diff) < ZONE_WIDTH_DEG / 2f
    }

    // Hold progress 0..1 (animated)
    var holdProgress by remember(
        lockpicking.id,
        lockpicking.unlockedCount
    ) { mutableFloatStateOf(0f) }
    val animatedHoldProgress by animateFloatAsState(holdProgress)

    // Track whether we already triggered the unlock to avoid double-calls
    var unlockTriggered by remember(lockpicking.id, lockpicking.unlockedCount) {
        mutableStateOf(
            false
        )
    }

    // Countdown timer
    val timeLimitSeconds = lockpicking.timeLimitSeconds
    var remainingSeconds by remember(lockpicking.id) {
        val initial = if (timeLimitSeconds != null) {
            val elapsed = (Clock.System.now() - lockpicking.createdAt).inWholeSeconds.toInt()
            (timeLimitSeconds - elapsed).coerceAtLeast(0)
        } else Int.MAX_VALUE
        mutableIntStateOf(initial)
    }

    val isActive = lockpicking.status == LockpickingStatus.ACTIVE
    val minimizeAlignment = Alignment.TopEnd

    // Countdown effect
    LaunchedEffect(lockpicking.id) {
        if (timeLimitSeconds != null && isActive) {
            while (remainingSeconds > 0) {
                delay(1000)
                val elapsed = (Clock.System.now() - lockpicking.createdAt).inWholeSeconds.toInt()
                remainingSeconds = (timeLimitSeconds - elapsed).coerceAtLeast(0)
                if (remainingSeconds <= 0) {
                    onTimeout()
                    break
                }
            }
        }
    }

    // Hold progress effect: when in zone, animate to 1; when out, reset to 0
    LaunchedEffect(isInZone, lockpicking.unlockedCount) {
        if (isInZone && isActive && !unlockTriggered) {
            val startTime = Clock.System.now()
            while (holdProgress < 1f) {
                delay(16.milliseconds)
                val elapsed = (Clock.System.now() - startTime).inWholeMilliseconds
                holdProgress = (elapsed.toFloat() / HOLD_DURATION_MS).coerceIn(0f, 1f)
                if (holdProgress >= 1f && !unlockTriggered) {
                    unlockTriggered = true
                    onUnlock()
                    break
                }
            }
        } else {
            holdProgress = 0f
        }
    }

    // Color scheme
    val primaryColor = MaterialTheme.colorScheme.primary
    val outlineColor = MaterialTheme.colorScheme.outline
    val successColor = MaterialTheme.colorScheme.success
    val errorColor = MaterialTheme.colorScheme.error

    Box(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.scrim.copy(alpha = 0.92f)),
        contentAlignment = Alignment.Center
    ) {
        Surface(
            modifier = Modifier
                .safeDrawingPadding()
                .fillMaxWidth(0.92f)
                .fillMaxHeight(0.9f)
                .padding(16.dp),
            shape = RoundedCornerShape(16.dp),
            color = MaterialTheme.colorScheme.surface,
            shadowElevation = 8.dp
        ) {
            Column(
                modifier = Modifier.fillMaxSize().padding(24.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.spacedBy(16.dp, Alignment.CenterVertically)
            ) {
                // Countdown timer
                if (timeLimitSeconds != null && isActive && remainingSeconds < Int.MAX_VALUE) {
                    val isUrgent = remainingSeconds <= 10
                    Text(
                        text = formatLockpickingCountdown(remainingSeconds),
                        style = MaterialTheme.typography.headlineMedium,
                        color = if (isUrgent) errorColor else primaryColor,
                        fontWeight = FontWeight.Bold
                    )
                }

                // Status messages
                when (lockpicking.status) {
                    LockpickingStatus.SUCCESS -> {
                        Text(
                            text = stringResource(Res.string.lockpicking_success_message),
                            style = MaterialTheme.typography.titleLarge,
                            color = successColor,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                    }

                    LockpickingStatus.TIMEOUT -> {
                        Text(
                            text = stringResource(Res.string.lockpicking_timeout_message),
                            style = MaterialTheme.typography.titleLarge,
                            color = errorColor,
                            fontWeight = FontWeight.Bold,
                            textAlign = TextAlign.Center
                        )
                    }

                    else -> {}
                }

                // Circular lock canvas (only when active)
                if (isActive) {
                    // Lock count indicator: dots above the lock
                    if (lockpicking.showLockCount) {
                        Row(
                            horizontalArrangement = Arrangement.spacedBy(8.dp),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            repeat(lockpicking.lockCount) { i ->
                                Box(
                                    modifier = Modifier
                                        .size(10.dp)
                                        .background(
                                            color = if (i < lockpicking.unlockedCount) successColor else outlineColor,
                                            shape = CircleShape
                                        )
                                )
                            }
                        }
                    }

                    Box(
                        modifier = Modifier.size(240.dp),
                        contentAlignment = Alignment.Center
                    ) {
                        // Lock image in background, covering the full circle
                        Image(
                            painter = painterResource(lockImageRes),
                            contentDescription = null,
                            modifier = Modifier.fillMaxSize()
                        )

                        Canvas(
                            modifier = Modifier
                                .fillMaxSize()
                                .pointerInput(lockpicking.id, lockpicking.unlockedCount) {
                                    var lastPosition: Offset? = null
                                    detectDragGestures(
                                        onDragStart = { offset ->
                                            lastPosition = offset
                                        },
                                        onDrag = { change, _ ->
                                            val center = Offset(size.width / 2f, size.height / 2f)
                                            val current = change.position
                                            pickAngle = computeAngle(center, current)
                                            lastPosition = current
                                        },
                                        onDragEnd = {
                                            lastPosition = null
                                        },
                                        onDragCancel = {
                                            lastPosition = null
                                        }
                                    )
                                }
                        ) {
                            val center = Offset(size.width / 2f, size.height / 2f)
                            val outerRadius = size.minDimension / 2f - 16.dp.toPx()
                            val strokeWidth = 8.dp.toPx()

                            // Outer ring (lock body)
                            drawCircle(
                                color = outlineColor,
                                radius = outerRadius,
                                center = center,
                                style = Stroke(width = strokeWidth),
                                alpha = 0.1f
                            )

                            // Target zone arc — only visible when pick is in zone
                            val zoneStartAngle = (targetZoneAngle + 180f) - ZONE_WIDTH_DEG / 2f - 90f
                            if (isInZone) {
                                drawArc(
                                    color = successColor.copy(alpha = 0.8f),
                                    startAngle = zoneStartAngle,
                                    sweepAngle = ZONE_WIDTH_DEG,
                                    useCenter = false,
                                    topLeft = Offset(
                                        center.x - outerRadius,
                                        center.y - outerRadius
                                    ),
                                    size = Size(outerRadius * 2, outerRadius * 2),
                                    style = Stroke(
                                        width = strokeWidth * 1.5f,
                                        cap = StrokeCap.Round
                                    )
                                )
                            }

                            // Hold progress arc (overlaid on zone)
                            if (isInZone && animatedHoldProgress > 0f) {
                                drawArc(
                                    color = successColor,
                                    startAngle = zoneStartAngle,
                                    sweepAngle = ZONE_WIDTH_DEG * animatedHoldProgress,
                                    useCenter = false,
                                    topLeft = Offset(
                                        center.x - outerRadius,
                                        center.y - outerRadius
                                    ),
                                    size = Size(outerRadius * 2, outerRadius * 2),
                                    style = Stroke(width = strokeWidth * 2f, cap = StrokeCap.Round)
                                )
                            }
                        }

                        // Pick image (crochet): top of image is at lock center, pivots from there
                        Image(
                            painter = painterResource(pickImageRes),
                            contentDescription = null,
                            modifier = Modifier
                                .align(Alignment.TopCenter)
                                .offset(y = 120.dp)
                                .graphicsLayer {
                                    transformOrigin = TransformOrigin(0.5f, 0f)
                                    rotationZ = (pickAngle + 180f) % 360f
                                }
                        )
                    }
                }

                Spacer(modifier = Modifier.height(4.dp))
            }
        }

        // Close button at the bottom for all terminal states
        if (!isActive) {
            Button(
                onClick = onClose,
                modifier = Modifier
                    .align(Alignment.BottomCenter)
                    .safeDrawingPadding()
                    .padding(bottom = 96.dp)
                    .width(200.dp)
            ) {
                Text(stringResource(Res.string.lockpicking_close))
            }
        }

        // Minimize button (only when active)
        if (isActive) {
            FilledIconButton(
                onClick = onMinimize,
                modifier = Modifier
                    .align(minimizeAlignment)
                    .safeDrawingPadding()
                    .padding(16.dp)
                    .size(48.dp)
            ) {
                Icon(
                    imageVector = Icons.Default.Minimize,
                    contentDescription = stringResource(Res.string.lockpicking_minimize)
                )
            }
        }
    }
}

/**
 * Returns a random angle in [0, 360) that is at least [excludeMargin] degrees away from
 * [startAngle] (the pick's starting position), so the player must actually move the pick.
 */
private fun randomAngleExcludingStart(startAngle: Float, excludeMargin: Float): Float {
    val rangeSize = 360f - 2 * excludeMargin
    val raw = excludeMargin + Random.nextFloat() * rangeSize
    return (raw + startAngle) % 360f
}

private fun computeAngle(center: Offset, point: Offset): Float {
    val dx = point.x - center.x
    val dy = point.y - center.y
    val radians = atan2(dy, dx)
    val degrees = radians * (180f / PI.toFloat())
    return (degrees + 90f + 360f) % 360f
}

/**
 * Returns the signed angular difference from a to b, in range [-180, 180].
 */
private fun angleDiff(a: Float, b: Float): Float {
    var diff = (b - a + 360f) % 360f
    if (diff > 180f) diff -= 360f
    return diff
}

private fun formatLockpickingCountdown(totalSeconds: Int): String {
    val minutes = (totalSeconds / 60).toString().padStart(2, '0')
    val seconds = (totalSeconds % 60).toString().padStart(2, '0')
    return "$minutes:$seconds"
}
